// Countertop filters, laid out to match the Vanity UI spec:
//   Size   -> thumbnail + dimension bar + label, with a trailing "ALL" link
//   Sinks  -> bordered text buttons + trailing "ALL"
//   Type   -> bordered text buttons + trailing "ALL"
// followed by a divider before the product grid.

// "ALL" clears its filter (shows every option) and is styled as a real button,
// matching the Single/Double/Slim/Classic controls.
function AllButton({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 md:py-1.5 text-[13px] font-bold border transition-colors ${
        active
          ? "border-gray-800 bg-gray-100 text-gray-900"
          : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
      }`}
    >
      ALL
    </button>
  );
}

function OptionButton({ active, disabled, onClick, children }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`px-5 py-2.5 md:py-1.5 text-[13px] font-bold border transition-colors ${
        disabled
          ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed"
          : active
          ? "border-gray-800 bg-gray-100 text-gray-900"
          : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
      }`}
    >
      {children}
    </button>
  );
}

// A size choice: representative image, a dimension bar, then the size label.
function SizeOption({ size, image, active, onClick }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center flex-1 min-w-0 group">
      <div
        className={`w-full h-14 flex items-center justify-center px-1 border transition-colors ${
          active ? "border-gray-800" : "border-transparent group-hover:border-gray-300"
        }`}
      >
        {image ? (
          <img src={image} alt="" className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="w-full h-full bg-gray-100" />
        )}
      </div>

      {/* dimension bar: dot —————— dot */}
      <div className="flex items-center w-full px-1 mt-1.5">
        <span className="w-[5px] h-[5px] rounded-full bg-gray-600 shrink-0" />
        <span className="flex-1 h-px bg-gray-400" />
        <span className="w-[5px] h-[5px] rounded-full bg-gray-600 shrink-0" />
      </div>

      <span
        className={`text-[13px] mt-1 ${
          active ? "font-bold text-gray-900" : "font-semibold text-gray-700"
        }`}
      >
        {size}cm
      </span>
    </button>
  );
}

export default function FilterBar({
  filters,
  onChange,
  sizes,
  sinks,
  thicknesses,
  sizeImages = {},
  doubleAllowed = true,
}) {
  const set = (key) => (val) => onChange({ ...filters, [key]: val });
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div className="px-5 pt-4">
      {/* ── Size ── */}
      <p className="text-sm text-gray-400 mb-2">Size</p>
      <div className="flex items-start gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {sizes.map((s) => (
            <SizeOption
              key={s}
              size={s}
              image={sizeImages[s]}
              active={filters.size === s}
              onClick={() => set("size")(filters.size === s ? null : s)}
            />
          ))}
        </div>
        <div className="pt-4 pl-1 shrink-0">
          <AllButton active={filters.size == null} onClick={() => set("size")(null)} />
        </div>
      </div>

      {/* ── Sinks + Type ── */}
      <div className="flex flex-wrap gap-x-8 gap-y-4 mt-5">
        <div>
          <p className="text-sm text-gray-400 mb-1.5">Sinks</p>
          <div className="flex items-center gap-2">
            {sinks.map((s) => (
              <OptionButton
                key={s}
                active={filters.sinks === s}
                disabled={s === "double" && !doubleAllowed}
                onClick={() => set("sinks")(s)}
              >
                {cap(s)}
              </OptionButton>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm text-gray-400 mb-1.5">Type</p>
          <div className="flex items-center gap-2">
            {thicknesses.map((t) => (
              <OptionButton
                key={t}
                active={filters.thickness === t}
                onClick={() => set("thickness")(t)}
              >
                {cap(t)}
              </OptionButton>
            ))}
          </div>
        </div>
      </div>

      <hr className="border-gray-200 mt-5" />
    </div>
  );
}
